<script setup lang="ts">
  import { Modal } from 'bootstrap'
  import InputComponent from '@/components/Atoms/Input.vue'
  import IconComponent from '../../../components/Atoms/Icon.vue'
  import PasswordValidation from '../components/Molecules/PasswordValidation.vue'

  import { ref, onMounted, computed, watch } from 'vue'

  const modal = ref<InstanceType<typeof Modal> | null>(null)
  const password = ref('')
  const passwordError = ref('')
  const repeatedPassword = ref('')
  const repeatedPasswordError = ref('')
  const repeatedPasswordIsBeingEdited = ref(false)
  const passwordIsValid = ref(false)

  const passwordsAreEqual = computed(
    () => password.value === repeatedPassword.value,
  )
  const canProceed = computed(
    () => passwordIsValid.value && passwordsAreEqual.value,
  )

  const emit = defineEmits(['proceed'])

  watch(repeatedPassword, (current) => {
    if (current === '' || current === password.value) {
      repeatedPasswordError.value = ''
      passwordError.value = ''
      return
    }

    if (current !== password.value) {
      repeatedPasswordError.value = 'As senhas não correspondem.'
      passwordError.value = 'As senhas não correspondem.'
    }
  })

  watch(repeatedPasswordIsBeingEdited, (current) => {
    if (!current) {
      repeatedPasswordError.value = ''
      passwordError.value = ''
      return
    }

    if (repeatedPassword.value === '') {
      repeatedPasswordError.value = ''
      passwordError.value = ''
      return
    }

    if (repeatedPassword.value !== password.value) {
      repeatedPasswordError.value = 'As senhas não correspondem.'
      passwordError.value = 'As senhas não correspondem.'
    }
  })

  onMounted(() => {
    modal.value = new Modal('#modal-set-new-password', {
      backdrop: 'static',
      keyboard: false,
    })
  })

  function show() {
    modal.value?.show()
  }

  function hide() {
    modal.value?.hide()
  }

  function proceed() {
    emit('proceed', password.value)
    hide()
  }

  defineExpose({ show, hide })
</script>

<template>
  <div id="modal-set-new-password" class="modal modal-md fade" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header pb-2">
          <h5 class="modal-title">Senha de uso</h5>
        </div>
        <div class="modal-body py-3">
          <p class="fs-6 text-start">
            Sua senha deve ter entre <strong>8 e 12 caracteres</strong>, ter
            pelo menos uma letra <strong>maíuscula</strong>, uma letra
            <strong>minúscula</strong> e <strong>números</strong>.
          </p>

          <div class="password-input-container mb-3">
            <div class="d-flex justify-content-start align-items-center pb-2">
              <span class="fs-6 pe-2">Senha de uso *</span>
            </div>

            <input-component
              v-model="password"
              icon="lock"
              type="password"
              placeholder="Digite sua senha aqui"
              :error-log="passwordError"
            />
          </div>

          <div class="password-validation-wrapper">
            <password-validation
              v-model="passwordIsValid"
              :password="password"
            />
          </div>

          <div class="password-input-container mt-3">
            <div class="d-flex justify-content-start align-items-center pb-2">
              <span class="fs-6 pe-2">Repetir senha de uso *</span>
            </div>

            <input-component
              v-model="repeatedPassword"
              icon="lock"
              type="password"
              placeholder="Digite sua senha aqui"
              :error-log="repeatedPasswordError"
              @focused="repeatedPasswordIsBeingEdited = true"
              @blured="repeatedPasswordIsBeingEdited = false"
            />

            <div class="alert alert-info mt-4" role="alert">
              <div>
                <div class="alert__icon">
                  <icon-component name="bell" />
                </div>
              </div>

              <div class="alert__content text-start">
                <span class="text">
                  <strong>A senha de uso será solicitada</strong>
                  sempre que você utilizar o seu Certificado Digital.
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer py-3">
          <button
            type="button"
            class="btn"
            :class="[canProceed ? 'btn-primary' : 'btn-secondary']"
            :disabled="!canProceed"
            @click="proceed"
          >
            Cadastrar e avançar
            <icon-component name="arrow-right" class="ms-2" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  @media (min-width: 576px) {
    .modal-md {
      --bs-modal-width: 440px;
    }
  }
</style>
